import type { Channel } from 'pusher-js';

import pusherClient from '~/config/initPusher';
import { logError } from '~/common/utils/log';
import { hasOrderChannelSubscribers } from '~/domains/market/domains/order/services/orderChannel';

import { useNormalizedMarketsStore } from '../state/normalizedMarkets.store';
import { transformPusherOrderbook } from '../utils/pricing';

import type { PublicPredictionMarket } from './interfaces/market.types';
import type { PusherMarketUpdatePayload } from './interfaces/market.pusher.types';
import { normalizeMoneylineMarket } from './interfaces/market.types';

const MARKET_UPDATE_EVENT = 'update';
const CHANNEL_PREFIX = 'cache-prediction-market-';

export type MarketUpdateInterceptor = (
  marketId: string,
  market: PublicPredictionMarket,
  timestamp?: number
) => boolean;

type PendingMarketUpdate = {
  marketId: string;
  market: PublicPredictionMarket;
  timestamp?: number;
};

export function getMarketChannelName(marketId: string): string {
  return `${CHANNEL_PREFIX}${marketId}`;
}

class MarketLiveUpdatesManager {
  private subscriptions = new Map<string, Set<string>>();

  private channels = new Map<string, Channel>();

  private interceptors = new Map<string, Set<MarketUpdateInterceptor>>();

  private isDisconnectedForVisibility = false;

  private pendingUpdates = new Map<string, PendingMarketUpdate>();

  private batchScheduled = false;

  subscribe(source: string, marketId: string): void {
    const channelName = getMarketChannelName(marketId);
    const sources = this.subscriptions.get(channelName) ?? new Set();

    if (sources.has(source)) {
      return;
    }

    if (sources.size === 0) {
      try {
        const channel = pusherClient.subscribe(channelName);
        channel.bind(MARKET_UPDATE_EVENT, this.handleUpdate);
        this.channels.set(channelName, channel);
      } catch (error) {
        logError('MarketLiveUpdatesManager: Failed to subscribe', { error, channelName, source });
        return;
      }
    }

    sources.add(source);
    this.subscriptions.set(channelName, sources);
  }

  unsubscribe(source: string, marketId: string): void {
    const channelName = getMarketChannelName(marketId);
    const sources = this.subscriptions.get(channelName);

    if (!sources) {
      return;
    }

    sources.delete(source);

    if (sources.size === 0) {
      const channel = this.channels.get(channelName);
      if (channel) {
        channel.unbind(MARKET_UPDATE_EVENT, this.handleUpdate);
        pusherClient.unsubscribe(channelName);
        this.channels.delete(channelName);
      }
      this.subscriptions.delete(channelName);
    }
  }

  registerInterceptor(marketId: string, interceptor: MarketUpdateInterceptor): () => void {
    const existing = this.interceptors.get(marketId) ?? new Set();
    existing.add(interceptor);
    this.interceptors.set(marketId, existing);

    return () => {
      const current = this.interceptors.get(marketId);
      if (current) {
        current.delete(interceptor);
        if (current.size === 0) {
          this.interceptors.delete(marketId);
        }
      }
    };
  }

  private handleUpdate = (data: PusherMarketUpdatePayload): void => {
    const { message: market, metadata } = data;
    const marketId = metadata?.id ?? market.id;
    const timestamp = market.updated_at;

    if (timestamp !== undefined) {
      const existingTimestamp = useNormalizedMarketsStore.getState().marketTimestamps.get(marketId);
      if (existingTimestamp !== undefined && timestamp <= existingTimestamp) {
        return;
      }
    }

    const transformedMarket = normalizeMoneylineMarket({
      ...market,
      orderbook: market.orderbook ? transformPusherOrderbook(market.orderbook) : undefined,
    } as PublicPredictionMarket);

    const marketInterceptors = this.interceptors.get(marketId);
    if (marketInterceptors) {
      for (const interceptor of marketInterceptors) {
        if (interceptor(marketId, transformedMarket, timestamp)) {
          return;
        }
      }
    }

    this.pendingUpdates.set(marketId, {
      marketId,
      market: transformedMarket,
      timestamp,
    });
    this.scheduleFlush();
  };

  private scheduleFlush(): void {
    if (!this.batchScheduled) {
      this.batchScheduled = true;
      requestAnimationFrame(() => {
        this.flushUpdates();
        this.batchScheduled = false;
      });
    }
  }

  private flushUpdates(): void {
    if (this.pendingUpdates.size === 0) {
      return;
    }

    const updates = Array.from(this.pendingUpdates.values());
    this.pendingUpdates.clear();

    useNormalizedMarketsStore.getState().updateMarketsBatch(updates);
  }

  getSubscriptionCount(): number {
    return this.subscriptions.size;
  }

  getSourcesForMarket(marketId: string): string[] {
    const channelName = getMarketChannelName(marketId);
    const sources = this.subscriptions.get(channelName);
    return sources ? Array.from(sources) : [];
  }

  handleVisibilityChange(isVisible: boolean): void {
    if (!isVisible && !this.isDisconnectedForVisibility) {
      if (hasOrderChannelSubscribers()) return;

      pusherClient.disconnect();
      this.isDisconnectedForVisibility = true;
    } else if (isVisible && this.isDisconnectedForVisibility) {
      pusherClient.connect();
      this.isDisconnectedForVisibility = false;
    }
  }

  hasActiveSubscriptions(): boolean {
    return this.subscriptions.size > 0;
  }

  simulateUpdate(data: PusherMarketUpdatePayload): void {
    this.handleUpdate(data);
  }
}

export const marketLiveUpdatesManager = new MarketLiveUpdatesManager();
